/**
 * THPT App Root Component
 *
 * Main app component with routing
 */

import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { LandingPage } from './pages/LandingPage';
import { AssessmentHistoryPage } from './pages/AssessmentHistoryPage';
import { ExamLibraryPage } from './pages/ExamLibraryPage';
import { ExamDetailPage } from './pages/ExamDetailPage';
import DemoExamPage from './pages/DemoExamPage';
import ExamResultsPage from './pages/ExamResultsPage';
import AuthPage from './pages/AuthPage';
import CareerOrientationPage from './pages/CareerOrientationPage';
import AuthCallback from './pages/AuthCallback';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { FullReportPage } from './pages/FullReportPage';
import { AccountManagementPage } from './pages/account/AccountManagementPage';
import CareerExplorationPage from './pages/CareerExplorationPage';
import CATProfilePage from './pages/CATProfilePage';
import CATProfileTeaserPage from './pages/CATProfileTeaserPage';
import CareerOrientationTeaserPage from './pages/CareerOrientationTeaserPage';
import PublicCATProfilePage from './pages/PublicCATProfilePage';
import MajorsListPage from './pages/MajorsListPage';
import { Toaster } from 'sonner';
import { useDarkMode } from './hooks/useDarkMode';
import { FeedbackWidget, LiveActivityToast } from '@vsat/shared/components';

// Policy Pages
import { AboutPage } from './pages/policies/AboutPage';
import { PaymentPolicyPage } from './pages/policies/PaymentPolicyPage';
import { DeliveryPolicyPage } from './pages/policies/DeliveryPolicyPage';
import { RefundPolicyPage } from './pages/policies/RefundPolicyPage';
import { PrivacyPolicyPage } from './pages/policies/PrivacyPolicyPage';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, accessToken } = useAuthStore();

  // Check both store state and localStorage for token
  // This handles race conditions where state hasn't updated yet
  const tokenFromStorage = localStorage.getItem('accessToken');
  const hasToken = accessToken || tokenFromStorage;

  // Require both auth flag and a valid token to avoid leaking pages when state is stale
  if (!isAuthenticated || !hasToken) {
    return <Navigate to='/login' replace />;
  }

  // If we have token in storage but not in state, sync it
  if (tokenFromStorage && !accessToken) {
    useAuthStore.getState().setToken(tokenFromStorage);
  }

  return <>{children}</>;
}

// Redirect old result URLs to new short format
const RedirectToNewResultUrl = () => {
  const { resultId } = useParams();
  return <Navigate to={`/ket-qua/${resultId}`} replace />;
};

// Public Only Route wrapper (redirect to assessment-history if logged in)
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, accessToken } = useAuthStore();
  const redirectTarget =
    localStorage.getItem('redirectAfterLogin') || localStorage.getItem('redirectUrl');

  // If already authenticated with a token, keep users within the app
  if (isAuthenticated && accessToken) {
    if (redirectTarget) {
      // Clean up redirect hints once consumed
      localStorage.removeItem('redirectAfterLogin');
      localStorage.removeItem('redirectUrl');
      return <Navigate to={redirectTarget} replace />;
    }

    // Default to intro screen (not instruction) to avoid auth guard triggers
    // The intro screen is public and won't cause redirect loops
    return <Navigate to='/huong-nghiep-cat?screen=intro' replace />;
  }

  return <>{children}</>;
}

import { ScrollToTop } from './components/ScrollToTop';

// Profile Route Guard - shows full page for authenticated users, marketing teaser for guests
function ProfileRouteGuard() {
  const { isAuthenticated, accessToken } = useAuthStore();
  const tokenFromStorage = localStorage.getItem('accessToken');
  const hasToken = accessToken || tokenFromStorage;

  if (isAuthenticated && hasToken) {
    return <CATProfilePage />;
  }

  return <CATProfileTeaserPage />;
}

// Career Orientation Route Guard - shows teaser for guests, full page for authenticated
function CareerOrientationRouteGuard() {
  const { isAuthenticated, accessToken } = useAuthStore();
  const tokenFromStorage = localStorage.getItem('accessToken');
  const hasToken = accessToken || tokenFromStorage;

  if (isAuthenticated && hasToken) {
    return <CareerOrientationPage />;
  }

  return <CareerOrientationTeaserPage />;
}

import { GlobalExamReminder } from './components/exam/GlobalExamReminder';
import { SchoolInfoReminder } from './components/account/SchoolInfoReminder';
import { CatMascot } from './components/CatMascot';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { initAutoPageViewTracking } from './telemetry/googleAnalytics';

function App() {
  useEffect(() => {
    const cleanup = initAutoPageViewTracking();
    return cleanup;
  }, []);
  const { isDark } = useDarkMode();
  const location = useLocation();
  // Hide mascot and feedback widget on exam pages to avoid distraction and button overlap
  const isExamPage = location.pathname.startsWith('/lam-bai');

  return (
    <div className='flex flex-col min-h-screen w-full overflow-x-hidden'>
      <ScrollToTop />
      <GlobalExamReminder />
      <SchoolInfoReminder />
      {!isExamPage && <CatMascot />}
      {!isExamPage && <LiveActivityToast />}
      <Toaster richColors position='bottom-right' theme={isDark ? 'dark' : 'light'} />
      <Routes>
        {/* OAuth Callback - Standalone */}
        <Route path='/auth/callback' element={<AuthCallback />} />

        {/* Hướng nghiệp CAT - Auth guard: guests see teaser, authenticated see full page */}
        <Route path='/huong-nghiep-cat' element={<CareerOrientationRouteGuard />} />
        {/* Legacy route redirect for backward compatibility */}
        <Route path='/career-orientation' element={<Navigate to='/huong-nghiep-cat' replace />} />

        {/* Deprecated: /cat-assessment not used - removed to avoid route conflicts */}

        {/* Assessment History - Has its own header/footer */}
        <Route
          path='/lich-su-danh-gia'
          element={
            <ProtectedRoute>
              <AssessmentHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/bao-cao-day-du'
          element={
            <ProtectedRoute>
              <FullReportPage />
            </ProtectedRoute>
          }
        />

        {/* CAT Profile - Public: guests see marketing teaser, authenticated see full page */}
        <Route path='/ho-so-nang-luc' element={<ProfileRouteGuard />} />

        {/* Public Routes - Pages have their own UnifiedHeader/Footer */}
        <Route path='/ho-so-nang-luc/s/:shareToken' element={<PublicCATProfilePage />} />
        <Route path='/mo-khoa' element={<Navigate to='/ho-so-nang-luc' replace />} />
        <Route path='/' element={<LandingPage />} />
        <Route path='/nganh-hoc' element={<MajorsListPage />} />
        <Route path='/kham-pha-nghe-nghiep' element={<CareerExplorationPage />} />

        {/* Policy Pages - Required for payment gateway */}
        <Route path='/gioi-thieu' element={<AboutPage />} />
        <Route path='/chinh-sach-thanh-toan' element={<PaymentPolicyPage />} />
        <Route path='/chinh-sach-giao-nhan' element={<DeliveryPolicyPage />} />
        <Route path='/chinh-sach-hoan-tien' element={<RefundPolicyPage />} />
        <Route path='/chinh-sach-bao-mat' element={<PrivacyPolicyPage />} />

        {/* Auth Page - Pages have their own UnifiedHeader/Footer */}
        <Route
          path='/login'
          element={
            <PublicOnlyRoute>
              <AuthPage />
            </PublicOnlyRoute>
          }
        />

        <Route
          path='/register'
          element={
            <PublicOnlyRoute>
              <AuthPage />
            </PublicOnlyRoute>
          }
        />

        {/* Legacy routes - redirect to new Vietnamese paths */}
        <Route path='/assessment-history' element={<Navigate to='/lich-su-danh-gia' replace />} />
        <Route path='/full-report' element={<Navigate to='/bao-cao-day-du' replace />} />
        <Route
          path='/career-exploration'
          element={<Navigate to='/kham-pha-nghe-nghiep' replace />}
        />

        {/* Exam routes - Vietnamese paths */}
        {/* Public routes - no login required to browse */}
        <Route path='/thi-thu-thpt' element={<ExamLibraryPage />} />
        <Route path='/thi-thu-thpt/:examId' element={<ExamDetailPage />} />
        <Route
          path='/lam-bai/:examId'
          element={
            <ProtectedRoute>
              <DemoExamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/results/:examId'
          element={
            <ProtectedRoute>
              <ExamResultsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/ket-qua/:resultId'
          element={
            <ProtectedRoute>
              <ExamResultsPage />
            </ProtectedRoute>
          }
        />
        {/* Backward compat: old /results/:examId/result/:resultId */}
        <Route path='/results/:examId/ket-qua/:resultId' element={<RedirectToNewResultUrl />} />
        {/* Legacy exam routes - redirect to Vietnamese paths */}
        <Route path='/exams' element={<Navigate to='/thi-thu-thpt' replace />} />
        <Route path='/exams/:examId' element={<Navigate to='/thi-thu-thpt' replace />} />
        <Route path='/exam-session/:examId' element={<Navigate to='/lam-bai' replace />} />
        <Route
          path='/account'
          element={
            <ProtectedRoute>
              <AccountManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </div>
  );
}

export default App;
